import type { ItineraryImportDraftPlace } from '@/models/itineraryImportTypes';
import type {
  SocialImportReviewPlace,
  SocialImportReviewSection,
} from '@/components/SocialImport/SocialImportPlaceReview';

export type TripImportReviewFilter = 'all' | 'bookable' | 'custom';

export function isTravelImportPlace(place: ItineraryImportDraftPlace): boolean {
  return place.row_kind === 'travel';
}

export function isBookableImportPlace(place: ItineraryImportDraftPlace): boolean {
  if (isTravelImportPlace(place)) return false;
  const cat = place.category ?? place.location?.gt_category;
  return Boolean(place.location && (cat === 'stay' || cat === 'do'));
}

export function isCustomImportPlace(place: ItineraryImportDraftPlace): boolean {
  return !isBookableImportPlace(place);
}

export function countReviewFilterPlaces(places: ItineraryImportDraftPlace[]): {
  all: number;
  bookable: number;
  custom: number;
} {
  let bookable = 0;
  places.forEach((place) => {
    if (isBookableImportPlace(place)) bookable += 1;
  });
  return { all: places.length, bookable, custom: places.length - bookable };
}

/** Add or remove only the visible ids. Other selections stay. */
export function toggleVisiblePlaceSelection(
  selected: Set<string>,
  visibleIds: string[],
): Set<string> {
  const next = new Set(selected);
  const allVisibleSelected =
    visibleIds.length > 0 && visibleIds.every((id) => next.has(id));
  visibleIds.forEach((id) => {
    if (allVisibleSelected) next.delete(id);
    else next.add(id);
  });
  return next;
}

export function placeMatchesReviewFilter(
  place: ItineraryImportDraftPlace,
  filter: TripImportReviewFilter,
): boolean {
  if (filter === 'all') return true;
  if (filter === 'bookable') return isBookableImportPlace(place);
  return isCustomImportPlace(place);
}

export function filterTripImportReviewSections(
  sections: SocialImportReviewSection[],
  draft: { days: { places: ItineraryImportDraftPlace[] }[] },
  filter: TripImportReviewFilter,
): SocialImportReviewSection[] {
  if (filter === 'all') return sections;

  const placeById = new Map<string, ItineraryImportDraftPlace>();
  draft.days.forEach((day) => {
    day.places.forEach((place) => {
      placeById.set(place.client_id, place);
    });
  });

  return sections
    .map((section) => ({
      ...section,
      places: section.places.filter((row: SocialImportReviewPlace) => {
        const draftPlace = placeById.get(row.id);
        if (!draftPlace) return false;
        return placeMatchesReviewFilter(draftPlace, filter);
      }),
    }))
    .filter((section) => section.places.length > 0);
}
